import React, { useEffect, useState } from "react";
import { EditBlock } from "../EditBlock";
import { Button, Col, message, Row } from "antd";
import style from "./EditCompound.module.scss";
import { errorMessage, QuestionType } from "../../../../../service/service";
import {
    checkTopic,
    getEmptyTopicChoice,
    TopicType
} from "../../../../../service/homework";
import EditDanXuan from "../EditDanXuan";
import EditDuoxuan from "../EditDuoxuan";
import EditPanDuan from "../EditPanDuan";
import EditZhuguan from "../EditZhuguan";
import EditTianKong from "../EditTiankong";
import _ from "underscore";
import usePromiseCallback from "utils/usePromiseCallback";
import { deleteExamTopic } from "service/exam";

interface EditCompoundProps {
    topic: TopicType;
    onChange: (topic: TopicType) => void;
}

const blockOption = {
    hasAdd: false,
    hasDelete: true,
    hasSave: false
};

export default function EditCompound({ topic, onChange }: EditCompoundProps) {
    const [innerTopic, setInnerTopic] = useState(topic);

    useEffect(() => {
        setInnerTopic(topic);
    }, [topic]);

    const onTopicChange = (topic: TopicType) => {
        const newTopic = { ...innerTopic };
        newTopic.topics = newTopic.topics.map(item => {
            return item.tempTopicId === topic.tempTopicId ? topic : item;
        });
        const score = _.reduce(
            newTopic.topics,
            (memo, num) => {
                //@ts-ignore
                return memo + parseFloat(num.score);
            },
            0
        );
        newTopic.score = score;

        setInnerTopic(newTopic);
    };

    const addTopic = (type: QuestionType) => {
        const newTopic = { ...innerTopic };
        const topic = getEmptyTopicChoice(type);
        newTopic.topics.push(topic);
        setInnerTopic(newTopic);
    };

    const onDelete = usePromiseCallback((topic: TopicType) => {
        return deleteExamTopic([topic])
            .then(() => {
                const newTopic = { ...innerTopic };
                const index = newTopic.topics.findIndex(
                    item => item.tempTopicId === topic.tempTopicId
                );
                if (index >= 0) {
                    newTopic.topics.splice(index, 1);
                }
                setInnerTopic(newTopic);
            })
            .then(() => !!message.info("删除成功!"))
            .catch(e => message.error(errorMessage(e)));
    });

    const onCheck = (
        topic: TopicType
    ): { result: "success" | "error"; msg?: string } => {
        const checkOption = { trunk: false };
        if (topic.topicType === QuestionType.WanXingTianKong) {
            checkOption.trunk = true;
        }

        if (!topic.trunk) {
            return { result: "error", msg: "请填写题目内容!" };
        }

        if (topic.topics.length === 0) {
            return { result: "error", msg: "请添加题型！" };
        }

        for (const item of topic.topics) {
            const { result, msg } = checkTopic(item, checkOption);
            if (result === "error") {
                return { result, msg };
            }
        }

        return { result: "success" };
    };

    return (
        <EditBlock
            customCheckTopic={onCheck}
            autoScore={innerTopic.score}
            disableScore
            type={innerTopic.topicType}
            topic={innerTopic}
            onChange={onChange}>
            <EditCompoundPure
                innerTopic={innerTopic}
                onTopicChange={onTopicChange}
                onDelete={onDelete}
                addTopic={addTopic}
            />
        </EditBlock>
    );
}

export interface EditCompoundPureProps {
    innerTopic: TopicType;
    onTopicChange: (topic: TopicType) => void;
    onDelete: (topic: TopicType) => void;
    addTopic: (type: QuestionType) => void;
}

export function EditCompoundPure({
    addTopic,
    innerTopic,
    onDelete,
    onTopicChange
}: EditCompoundPureProps) {
    return (
        <React.Fragment>
            <Row>
                <Col span={24}>
                    {innerTopic.topics.map((topic, index) => (
                        <div key={index}>
                            {topic.topicType === QuestionType.DanXuan && (
                                <EditDanXuan
                                    onChangeNow={onTopicChange}
                                    blockOption={{
                                        onDelete: () => onDelete(topic),
                                        ...blockOption
                                    }}
                                    topic={topic}
                                />
                            )}
                            {topic.topicType === QuestionType.DuoXuan && (
                                <EditDuoxuan
                                    onChangeNow={onTopicChange}
                                    blockOption={{
                                        onDelete: () => onDelete(topic),
                                        ...blockOption
                                    }}
                                    topic={topic}
                                    onChange={topic => onTopicChange(topic)}
                                />
                            )}
                            {topic.topicType === QuestionType.PanDuan && (
                                <EditPanDuan
                                    onChangeNow={onTopicChange}
                                    blockOption={{
                                        onDelete: () => onDelete(topic),
                                        ...blockOption
                                    }}
                                    topic={topic}
                                    onChange={topic => onTopicChange(topic)}
                                />
                            )}
                            {topic.topicType === QuestionType.ZhuGuan && (
                                <EditZhuguan
                                    onChangeNow={onTopicChange}
                                    blockOption={{
                                        onDelete: () => onDelete(topic),
                                        ...blockOption
                                    }}
                                    topic={topic}
                                    onChange={topic => onTopicChange(topic)}
                                />
                            )}
                            {topic.topicType === QuestionType.TianKong && (
                                <EditTianKong
                                    onChangeNow={onTopicChange}
                                    blockOption={{
                                        onDelete: () => onDelete(topic),
                                        ...blockOption
                                    }}
                                    topic={topic}
                                    onChange={topic => onTopicChange(topic)}
                                />
                            )}
                        </div>
                    ))}
                </Col>
            </Row>
            <Row className={style.addTopic}>
                <Col span={3} className={style.label}>
                    添加题型：
                </Col>
                <Col span={21}>
                    <Col span={4}>
                        <Button
                            onClick={() => addTopic(QuestionType.DanXuan)}
                            icon="check-circle">
                            单选
                        </Button>
                    </Col>
                    <Col span={4}>
                        <Button
                            onClick={() => addTopic(QuestionType.DuoXuan)}
                            icon="check-square">
                            多选
                        </Button>
                    </Col>
                    <Col span={4}>
                        <Button
                            onClick={() => addTopic(QuestionType.PanDuan)}
                            icon="check">
                            判断
                        </Button>
                    </Col>
                    <Col span={4}>
                        <Button
                            onClick={() => addTopic(QuestionType.ZhuGuan)}
                            icon="align-left">
                            简答
                        </Button>
                    </Col>
                    <Col span={4}>
                        <Button
                            onClick={() => addTopic(QuestionType.TianKong)}
                            icon="column-width">
                            填空
                        </Button>
                    </Col>
                </Col>
            </Row>
        </React.Fragment>
    );
}
